import {OrderedMap, OrderedSet, List, Map} from 'immutable';
import _ from 'lodash';
import {reportBug} from '@packages/utilities/reporter';
import * as IXStateUtils from './IXStateUtils';
import * as StringUtils from '@packages/utilities/string-utils';
import {generateGuid} from '@packages/utilities/guid';
import {
  transformKeysort,
  IXMacro,
  IXMacroValue,
  IXStyle,
  IXStep,
  IXTrigger,
} from './IXState';

export function reifyIXMacros(rawMacros = []) {
  let newMacros = OrderedMap(rawMacros.map(reifyIXMacro));
  newMacros = IXStateUtils.getMacrosWith3dProps(newMacros);
  return newMacros;
}

export function reifyIXMacro(json = {}) {
  const {_id, value = {}} = json;
  return [
    _id,
    IXMacro({
      _id,
      name: json.name,
      slug: reifyIXSlug(json),
      pages: Map(json.pages),
      value: IXMacroValue({
        style: reifyIXStyle(value.style),
        triggers: reifyIXTriggers(value.triggers),
      }),
    }),
  ];
}

export function reifyIXSlug({name, slug}) {
  return slug || StringUtils.slugify(name) || generateGuid();
}

export function reifyIXStyle(json = {}) {
  let style = IXStyle();
  try {
    const {keysort, ...rest} = migrateLegacyTransforms(json);
    style = reifyKeysort({record: style, keysort, json: rest});
    style = style.merge(rest);
  } catch (err) {
    // Please see https://github.com/webflow/webflow/pull/39900
    // eslint-disable-next-line webflow/better-bug-reporting
    reportBug('IXImportError', 'Could not reify IX style', err);
  }
  return style;
}

function reifyIXTriggers(rawTriggers = []) {
  return List(rawTriggers.map(reifyIXTrigger));
}

export function reifyIXTrigger(json = {}) {
  let trigger = IXTrigger();
  try {
    const {stepsA, stepsB, ...rest} = repairCorruptLegacyTrigger(json);
    if (!json._id) {
      trigger = trigger.set('_id', generateGuid());
    }
    trigger = trigger.merge({
      ...rest,
      stepsA: reifyIXSteps(stepsA),
      stepsB: reifyIXSteps(stepsB),
    });
  } catch (err) {
    // Please see https://github.com/webflow/webflow/pull/39900
    // eslint-disable-next-line webflow/better-bug-reporting
    reportBug('IXImportError', 'Could not reify IX trigger', err);
  }
  return trigger;
}

function reifyIXSteps(rawSteps = []) {
  return List(rawSteps.map(reifyIXStep));
}

export function reifyIXStep(json = {}) {
  let step = IXStep();
  try {
    const {keysort, ...rest} = migrateLegacyTransforms(
      repairCorruptLegacyStep(json)
    );
    step = reifyKeysort({record: step, keysort, json: rest});
    if (!rest._id) {
      step = step.set('_id', generateGuid());
    }
    step = step.merge(rest);
  } catch (err) {
    // Please see https://github.com/webflow/webflow/pull/39900
    // eslint-disable-next-line webflow/better-bug-reporting
    reportBug('IXImportError', 'Could not reify IX step', err);
  }
  return step;
}

function reifyKeysort({record, keysort, json}) {
  if (_.isArray(keysort) && keysort.length) {
    return record.set('keysort', OrderedSet(keysort));
  } else {
    keysort = OrderedSet(Object.keys(json))
      .filter((key) => transformKeysort.has(key))
      .union(transformKeysort);
    return record.set('keysort', keysort);
  }
}

function repairCorruptLegacyStep(json) {
  // Steps should not have group or trigger properties
  if (_.has(json, 'group') || _.has(json, 'trigger')) {
    const result = _.cloneDeep(json);
    // eslint-disable-next-line webflow/no-delete
    delete result.group;
    // eslint-disable-next-line webflow/no-delete
    delete result.trigger;
    return result;
  }
  return json;
}

function repairCorruptLegacyTrigger(json) {
  // Triggers should not have a macro property
  if (_.has(json, 'macro')) {
    const result = _.cloneDeep(json);
    // eslint-disable-next-line webflow/no-delete
    delete result.macro;
    return result;
  }
  return json;
}

function migrateLegacyTransforms(json) {
  const {scale, rotate} = json;
  if (scale != null || rotate != null) {
    const result = _.cloneDeep(json);
    if (scale != null) {
      // eslint-disable-next-line webflow/no-delete
      delete result.scale;
      result.scaleX = scale;
      result.scaleY = scale;
      result.scaleZ = 1;
    }
    if (rotate != null) {
      // eslint-disable-next-line webflow/no-delete
      delete result.rotate;
      result.rotateX = '0deg';
      result.rotateY = '0deg';
      result.rotateZ = rotate;
    }
    return result;
  }
  return json;
}
